import "./edit-commodity-modal.css";

import { Button } from "@tql/ui-components-react/tql/buttons";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";

import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { SavedCommoditiesModal } from "~/shared/components/commodities/saved-commodities-modal.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { Commodity } from "~/shared/schemas/commodity-schema.ts";
import type { ShipmentLineItem } from "~/shared/schemas/shipment-management-schema.ts";
import type {
	HandlingUnit,
	OnChange,
} from "~/shared/types/quote-details-types";
import {
	calculateItemDensity,
	getEstClass,
} from "~/shared/utils/commodity-utils.ts";

import { shipmentDraftAtom } from "../../../../state/shipment-edit-atoms.ts";
import { FreightCommodityFields } from "./commodity-fields.tsx";
import { isBlankHandlingUnit } from "./is-blank-handling-unit.ts";
import { mapHandlingUnitToLineItem } from "./map-handling-unit-to-line-item.ts";
import { mapLineItemToHandlingUnit } from "./map-line-item-to-handling-unit.ts";
import { mapSavedCommodityToHandlingUnit } from "./map-saved-commodity-to-handling-unit.ts";
import { validateFreightCommodity } from "./validate-freight-commodity.ts";

// Shipment freight is always imperial.
const MEASURING_TYPE = "LB/IN";
const SORT_AND_SEGREGATE = "Sort and Segregate";

type EditCommoditiesModalProps = {
	isOpen: boolean;
	onClose: () => void;
	commodity: ShipmentLineItem | null;
	/** Receives one line item in edit mode, or every added unit in add mode. */
	onSave: (lineItems: Partial<ShipmentLineItem>[]) => void;
	mode?: "add" | "edit";
	/** 1-based position of the (first) unit on the shipment, shown as "Handling Unit N". */
	handlingUnitNumber: number;
	/** Shipment customer whose saved commodities can be added in add mode. */
	customerId?: number;
};

export const EditCommoditiesModal = ({
	isOpen,
	onClose,
	commodity,
	onSave,
	mode = "edit",
	handlingUnitNumber,
	customerId,
}: EditCommoditiesModalProps) => {
	const shipmentDraft = useAtomValue(shipmentDraftAtom);
	const { data } = useQuoteDetailsPageLoadQueries();
	const unitTypes = data?.unitTypes ?? [];
	// The shipment stores line items in a different shape than the editor's
	// `HandlingUnit`, so map on the way in.
	const [handlingUnits, setHandlingUnits] = useState<HandlingUnit[]>(() =>
		commodity ? [mapLineItemToHandlingUnit(commodity, unitTypes)] : [],
	);
	// Keyed by handling unit id so errors stay attached as units are added.
	const [validationErrors, setValidationErrors] = useState<
		Map<number, Set<string>>
	>(() => new Map());
	const [showSavedCommodities, setShowSavedCommodities] = useState(false);

	// Re-seed the editable copy whenever a different commodity is opened.
	useEffect(() => {
		setHandlingUnits(
			commodity ? [mapLineItemToHandlingUnit(commodity, unitTypes)] : [],
		);
		setValidationErrors(new Map());
		setShowSavedCommodities(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- unitTypes only needs to be fresh, not re-trigger the reseed
	}, [commodity]);

	const clearFieldError = (unitId: number, fieldKey: string) => {
		setValidationErrors((prev) => {
			const unitErrors = prev.get(unitId);
			if (!unitErrors?.has(fieldKey)) {
				return prev;
			}
			const nextUnitErrors = new Set(unitErrors);
			nextUnitErrors.delete(fieldKey);
			return new Map(prev).set(unitId, nextUnitErrors);
		});
	};

	const updateItem = (
		item: HandlingUnit,
		field: keyof HandlingUnit,
		value: OnChange | null,
	) => {
		setHandlingUnits((prev) =>
			prev.map((unit) => {
				if (unit.id !== item.id) {
					return unit;
				}
				const updated = { ...unit, [field]: value };

				if (
					field === "quantity" ||
					field === "weight" ||
					field === "length" ||
					field === "width" ||
					field === "height"
				) {
					updated.density = calculateItemDensity(updated, MEASURING_TYPE);
					updated.estClass = getEstClass(updated.density);
				}

				return updated;
			}),
		);
	};

	// Saved commodities replace the untouched blank unit rather than being
	// added after it, so the user isn't left with an empty row to fill in.
	const addSavedCommodities = (commodities: Commodity[]) => {
		const savedUnits = commodities.map((savedCommodity) =>
			mapSavedCommodityToHandlingUnit(savedCommodity, unitTypes),
		);
		setHandlingUnits((prev) => [
			...prev.filter((unit) => !isBlankHandlingUnit(unit)),
			...savedUnits,
		]);
	};

	if (!isOpen || handlingUnits.length === 0) {
		return null;
	}

	const isAddMode = mode === "add";
	const sortAndSegregateSelected =
		(shipmentDraft?.pickup?.accessorials ?? []).includes(SORT_AND_SEGREGATE) ||
		(shipmentDraft?.delivery?.accessorials ?? []).includes(SORT_AND_SEGREGATE);
	const requiresPieceCount = (unit: HandlingUnit) =>
		!!unit.isHazmat || sortAndSegregateSelected;

	const handleSave = () => {
		const errors = new Map<number, Set<string>>();
		for (const unit of handlingUnits) {
			const unitErrors = validateFreightCommodity(unit, {
				requirePieceCount: requiresPieceCount(unit),
			});
			if (unitErrors.size > 0) {
				errors.set(unit.id, unitErrors);
			}
		}
		if (errors.size > 0) {
			setValidationErrors(errors);
			return;
		}
		onSave(handlingUnits.map((unit) => mapHandlingUnitToLineItem(unit)));
		onClose();
	};

	return (
		<>
			<TraxModal
				cancelText={"Cancel"}
				className={"edit-commodity-modal"}
				confirmText={isAddMode ? "Add & close" : "Save changes"}
				onCancel={onClose}
				onClose={onClose}
				onConfirm={handleSave}
				showConfirmationButtons={true}
				title={isAddMode ? "Add Handling Unit" : "Edit Handling Unit"}
				type={"primary"}
			>
				{isAddMode ? (
					<div className={"edit-commodity-modal-toolbar"}>
						<Button
							data-testid={"edit-commodity-modal-saved-commodities"}
							disabled={!customerId}
							onClick={() => setShowSavedCommodities(true)}
							variant={"secondary"}
						>
							Saved Commodities
						</Button>
					</div>
				) : null}
				{handlingUnits.map((unit, index) => (
					<div key={unit.id} className={"edit-commodity-modal-unit"}>
						<FreightCommodityFields
							handlingUnit={unit}
							idx={handlingUnitNumber - 1 + index}
							measuringType={MEASURING_TYPE}
							onClearFieldError={(fieldKey) =>
								clearFieldError(unit.id, fieldKey)
							}
							requirePieceCount={requiresPieceCount(unit)}
							showHazmatDetails={true}
							updateItem={updateItem}
							validationErrors={validationErrors.get(unit.id)}
						/>
					</div>
				))}
			</TraxModal>
			{isAddMode ? (
				<SavedCommoditiesModal
					customerId={customerId}
					onSetSelectedItems={addSavedCommodities}
					onToggleModal={() => setShowSavedCommodities(false)}
					showModal={showSavedCommodities}
				/>
			) : null}
		</>
	);
};
